import Vue from 'vue';
import jwtDecode from 'jwt-decode';
import Cookies from 'js-cookie';
import qs from 'qs';

import ApiError from 'utils/api-error';

import StorageFactory from './storage/storage-factory'
import { isEmptyObject, objectExtend, compare } from './utils';
import defaults from './defaults';

import { clientRole, clientUserRole, clientProviderRole } from "@/constants";
import { tryStopSignalRConnection } from "@/services/signalr-service";

/**
* OpenID Connect/OAuth 2.0 auth manager.
*
* Handles user authentication and token-based authorization by using OpenID Connect/OAuth 2.0 standards.
*/
export default class AuthManager {

  constructor($http, options) {
    this.$http = $http;
    this.options = objectExtend(defaults, options);
    this.storage = StorageFactory(options);
    this.router = options.router;

    //define internal reactive state
    this.internalState = new Vue({
      data: function () {
        return {
          tokenData: null,
          userInfo: null,
        };
      }
    });

    //define getters and setters
    Object.defineProperties(this, {
      tokenData: {
        get() {
          if (!this.storage.getItem('tokenData')) return null;

          return JSON.parse(this.storage.getItem('tokenData'));
        },
        set(object) {
          this.internalState.tokenData = object;
          this.storage.setItem('tokenData', JSON.stringify(object));
        }
      },

      userInfo: {
        get() {
          if (!this.storage.getItem('userInfo')) return null;

          return JSON.parse(this.storage.getItem('userInfo'));
        },
        set(object) {
          this.internalState.userInfo = object;
          this.storage.setItem('userInfo', JSON.stringify(object));
        }
      }
    });

    //add Axios interceptors
    this.$http.interceptors.request.use(config => {

      if (this._tokenExists())
        this._setAuthHeader(config);

      return config;
    });

    this.$http.interceptors.response.use(response => {
      return response;
    },
      error => {
        if (this._isInvalidToken(error.response))
          return this._refreshToken(error.config);

        if (error.response) {
          if (error.response.status === 401) {
            this.router.push({ name: this.options.unauthorizedRedirectTo, query: { redirect: this.router.currentRoute.path } });

            if (error.response.headers["logout"])
              this.logout();
          }

          if (error.response.status === 403)
            this.router.replace({ name: this.options.forbiddenRedirectTo });
        }

        return Promise.reject(error);
      });
  }

  /**
   * Reactive getter - checks if user is authenticated.
   *
  * @return {boolean}
  */
  get isAuthenticated() {
    return this.internalState.tokenData != null;
  }

  /**
   * Reactive getter - User info received from userinfo endpoint.
   *
  * @return {Object} user info
  */
  get user() {
    if (!this.isAuthenticated)
      return null;

    return this.internalState.userInfo;
  }

  /**
   * Reactive getter - checks if admin is logged in as another user.
   *
  * @return {boolean}
  */
  get hasLocalAccount() {
    if (!this.isAuthenticated)
      return false;

    if (!this.user)
      return false;

    return this.user.has_local_account;
  }

  /**
   * Reactive getter - checks if admin is logged in as another user.
   *
  * @return {boolean}
  */
  get isLoggedInAsAnotherUser() {
    if (!this.isAuthenticated)
      return null;

    return this._decodeAccessToken().admin_username != null;
  }

  get isClientLoggedInAsPatient() {
    let loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));

    if (loggedInUser && loggedInUser.roles.some(r => r === clientRole || r === clientUserRole || r === clientProviderRole))
      return true;

    if (!this.isAuthenticated && this._decodeAccessToken().patient_info_hidden)
      return null;

    return Boolean(this._decodeAccessToken().patient_info_hidden);
  }

  get id() {
    return parseInt(this._decodeAccessToken().sub);
  }

  get role() {
    return this._decodeAccessToken().role;
  }

  get needsTwoFactorAuthentication() {
    return this._decodeAccessToken().is_two_factor_auth_needed === "True";
  }

  /**
   * Initialization
   *
   * Check if user session exists and initializes the authentication state.
   *
   * @return {void}
  */
  init(forceRedirectTo) {
    if (this._ensureDataIntegrity()) {
      //restore state from storage if exists
      this.internalState.tokenData = this.tokenData;
      this.internalState.userInfo = this.userInfo;
    }
    else
      this._endSession();

    this._initNavigationGuards(forceRedirectTo);
  }

  getFeature(identificator) {
    if (!this.isAuthenticated)
      return false;

    const features = this._decodeAccessToken()["features"]

    if (!features)
      return false;

    const parsedFeatures = JSON.parse(features.replace(/'/g, '"'));

    return parsedFeatures.find(f => f.Identifier === identificator);
  }

  isFeatureAvailable(identificator) {
    return !!this.getFeature(identificator);
  }

  /**
   * Login
   *
   * @param {string} username Username for logging in.
   * @param {string} password Password for logging in.
   * @param {boolean} rememberMe Remember user or not.
   * @param {string|null} redirect The route to redirect to.
   * @return {void}
   */
  async login(username, password, rememberMe, redirect, grantType = "password", accessToken = null, code = null, params = null) {
    const data = {
      grant_type: grantType,
      username: username,
      password: password,
      scope: this.options.scope,
      access_token: accessToken,
      code: code,
      ...params
    }

    await this._login(data, rememberMe, redirect);
  }

  /**
   * Updates user info
   *
   * @return {void}
   */
  async updateUserInfo() {
    try {
      let response = await this.$http.get(this.options.userInfoUri, { baseURL: '' });
      this._storeUserInfo(response);
    }
    catch (error) {
      throw new ApiError(error);
    }
  }

  /**
   * Logout
   *
   * Clear all data in storage (which resets logged-in status) and redirect.
   *
   * @return {void}
   */
  async logout() {
    if (this.tokenData)
      await this.$http.get(this.options.logoutUri, { baseURL: '' });

    this._endSession();

    localStorage.removeItem("loggedInUser");
    tryStopSignalRConnection();
  }

  /**
   * Check if user belongs to specific role.
   *
  * @param {string} role A role.
  * @return {boolean}
  */
  isInRole(role) {
    if (!this.isAuthenticated) {
      localStorage.removeItem("loggedInUser");
      
      return false;
    }
      

    return compare(role, this.role);
  }

  isRouteAccessible(route) {
    if (route.meta && route.meta.auth) {
      if (!this.isAuthenticated)
        return false;

      if (!this._tokenExists())
        return false;

      if (route.meta.auth.roles)
        return route.meta.auth.roles.some(r => this.isInRole(r));
    }

    return true;
  }

  /**
   * Internal login
   *
   * @param {object} data Data to send to the token endpoint.
   * @param {boolean} rememberMe Remember user or not.
   * @param {string|null} redirect The route to redirect to.
   * @return {void}
   */
  async _login(data, rememberMe, redirect) {
    try {
      let response = await this.$http.post(this.options.tokenUri, qs.stringify(data), { baseURL: '' });

      this._storeToken(response);
      this._clearDataTableCache();
      
      if (rememberMe)
        Cookies.set('userSessionMarker', 'userSessionMarker', { expires: this.options.rememberMeDuration });
      else
        Cookies.set('userSessionMarker', 'userSessionMarker');

      await this.updateUserInfo();

      let redirectUrl = typeof (redirect) == "function" ? redirect() : redirect;

      if (response.headers["redirect-to"])
        this.router.push(response.headers["redirect-to"])
      else if (redirectUrl)
        this.router.push(redirectUrl)
    }
    catch (error) {
      throw new ApiError(error);
    }
  }

  /**
  * Decodes the stored access token.
  *
  * @private
  * @return {object} Decoded access token
  */
  _decodeAccessToken() {
    return jwtDecode(this.internalState.tokenData.accessToken);
  }

  _clearDataTableCache() {
    Object.keys(localStorage).map((key) => {
      if (key.startsWith('DataTable-')) {
        localStorage.removeItem(key);
      }
    });
  }
  /**
  * Clears user's authentication data and redirect's to configured logoutRedirectTo route.
  *
  * @private
  * @return {void}
  */
  _endSession() {
    this._clearDataTableCache();

    Cookies.remove('userSessionMarker');

    this.tokenData = null;
    this.userInfo = null;

    this.router.replace({ name: this.options.logoutRedirectTo }).catch(() => {});
  }

  /**
  * Set the Authorization header on Axios Request.
  *
  * @private
  * @param {Object} config The Axios request's config to set the header on.
  * @return {void}
  */
  _setAuthHeader(config) {
    config.headers['Authorization'] = 'Bearer ' + this.tokenData.accessToken;
  }

  /**
   * Retry the original request.
   *
   * Let's retry the user's original target request that had recieved an invalid token response (which we fixed with a token refresh).
   *
   * @private
   * @param {Object} config The Axios request's config to use to repeat an http call.
   * @return {Promise}
   */
  async _retryAfterTokenRefresh(config) {
    //re-write the original request's access token with a refreshed one
    this._setAuthHeader(config);

    return await this.$http.request(config);
  }

  /**
   * Refresh the access token
   *
   * Make an ajax call to the OpenID Connect server to refresh the access token (using our refresh token).
   *
   * @private
   * @param {Object} config The Axios original request's config that we'll retry.
   * @return {Promise}
   */
  async _refreshToken(config) {
    const data = {
      grant_type: 'refresh_token',
      'refresh_token': this.tokenData.refreshToken
    }

    try {
      let response = await this.$http.post(this.options.tokenUri, qs.stringify(data), { baseURL: '' });

      this._storeToken(response);

      return this._retryAfterTokenRefresh(config);
    }
    catch (error) {
      this._endSession();
      throw error;
    }
  }

  /**
   * Store tokens
   *
   * Update the storage with the access/refresh tokens received from the token endpoint from the OpenID Connect server.
   *
   * @private
   * @param {Object} response Axios's response instance from the server that contains our tokens.
   * @return {void}
   */
  _storeToken(response) {
    const newTokenData = this.tokenData || {}

    newTokenData.accessToken = response.data.access_token

    if (response.data.refresh_token)
      newTokenData.refreshToken = response.data.refresh_token

    this.tokenData = newTokenData;
  }

  /**
   * Store user info
   *
   * Update the storage with the user info received from the userinfo endpoint from the OpenID Connect server.
   *
   * @private
   * @param {Object} response Axios's response instance from the server that contains userinfo.
   * @return {void}
   */
  _storeUserInfo(response) {
    this.userInfo = response.data && !isEmptyObject(response.data) ? response.data : null;
  }

  /**
   * Check if the Axios's response is an invalid token response.
   *
   * @private
   * @param {Object} response The Axios's response instance received from an http call.
   * @return {boolean}
   */
  _isInvalidToken(response) {
    if (!response)
      return false;

    if (!response.headers)
      return false;

    const status = response.status
    const wwwAuthenticateHeader = response.headers['www-authenticate']

    return (status === 401 && wwwAuthenticateHeader && (wwwAuthenticateHeader.includes('invalid_token') || wwwAuthenticateHeader.includes('expired_token')));
  }

  /**
   * Ensures data integrity.
   *
   * @private
   * @return {boolean}
   */
  _ensureDataIntegrity() {
    //if there is no token data then session marker should not exist
    if (!this.tokenData && Cookies.get('userSessionMarker'))
      return false;

    //if there is token data then session marker should exist as well
    if (this.tokenData && !Cookies.get('userSessionMarker'))
      return false;

    return true;
  }

  /**
   * Checks if token data exists.
   *
   * @private
   * @return {boolean}
   */
  _tokenExists() {
    return this._ensureDataIntegrity() && this.tokenData;
  }

  /**
  * Client-side authorization checks for navigation.
  *
  * @private
  * @return {boolean}
  */
  _initNavigationGuards(forceRedirectTo) {

    //user tries to open an direct URL or refreshes a browser
    this.router.onReady(() => {
      if (this.internalState.tokenData) {
        if (this.isClientLoggedInAsPatient && this.router.currentRoute.path !== '/survey') {
          this.router.push({ path: '/survey' });
        }

        if (this.needsTwoFactorAuthentication && this.router.currentRoute.name !== this.options.twoFactorRedirectTo) {
          this.router.push({ name: this.options.twoFactorRedirectTo });
          return;
        }
      }

      if (!this.isRouteAccessible(this.router.currentRoute)) {
        if (!this.isAuthenticated)
          this.router.push({ name: this.options.unauthorizedRedirectTo, query: { redirect: this.router.currentRoute.path } });
        else
          this.router.replace({ name: this.options.forbiddenRedirectTo });

        return;
      }

      if (forceRedirectTo) {
        let redirectTo = forceRedirectTo(this.router.currentRoute);

        if (redirectTo) {
          let resolved = this.router.resolve(redirectTo);

          if (resolved.route)
            this.router.push(resolved.route.path);
        }
      }
    });

    //user performs a regular navigation
    this.router.beforeEach((to, from, next) => {
      if (this.internalState.tokenData) {
        if (this.needsTwoFactorAuthentication && to.name !== this.options.twoFactorRedirectTo) {
          this.router.push({ name: this.options.twoFactorRedirectTo });
          return;
        }
      }

      if (!this.isRouteAccessible(to)) {
        next({ name: this.options.forbiddenRedirectTo });
        return;
      }

      if (forceRedirectTo) {
        let redirectTo = forceRedirectTo(to);

        if (redirectTo) {
          let resolved = this.router.resolve(redirectTo);

          if (resolved.route && resolved.route.path !== to.path) {
            next(resolved.route.path);
            return;
          }
        }
      }

      next();
    })
  }
}